import { Link, useParams } from "react-router-dom";
import SEOHead from "@/components/feature/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { getArticleById } from "@/mocks/article";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || "1", 10);
  const article = getArticleById(articleId);

  if (!article) {
    return (
      <>
        <SEOHead
          title="页面未找到 - 6789影视"
          description="抱歉，您要查找的页面不存在。"
          keywords="6789影视"
          canonicalUrl="https://6789ys.com/"
        />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-4xl text-foreground-950 mb-4">
              404
            </h1>
            <p className="text-foreground-600 mb-6">抱歉，该节目不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex px-6 py-3 rounded-full bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors no-underline"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.fullDescription,
    datePublished: article.publishDate,
    author: {
      "@type": "Organization",
      name: "6789影视",
    },
    publisher: {
      "@type": "Organization",
      name: "6789影视",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://6789ys.com/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 6789影视 | ${article.category}在线观看`}
        description={`${article.title}，${article.fullDescription.slice(0, 120)}...免费在线观看，高清流畅不缓冲。`}
        keywords={`${article.title},${article.category},${article.tags.join(",")},综艺,在线观看`}
        canonicalUrl={`https://6789ys.com/article/${article.id}`}
        ogType="article"
      />
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto py-4">
            <Breadcrumb
              items={[
                { name: "首页", path: "/" },
                {
                  name: article.category,
                  path: `/category/${article.categorySlug}`,
                },
                { name: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 lg:px-8 max-w-5xl mx-auto py-8 md:py-12">
          <div className="mb-8">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-600 mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.source}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views} 次观看
              </span>
              <span className="flex items-center gap-1 text-accent-500">
                <i className="ri-star-fill"></i>
                {article.rating}
              </span>
            </div>
          </div>

          <div className="relative rounded-xl overflow-hidden mb-8 aspect-video">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
              title={`${article.title} - 6789影视`}
            />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-white cursor-pointer hover:bg-primary-600 transition-colors">
                <i className="ri-play-fill text-3xl md:text-4xl ml-1"></i>
              </div>
            </div>
            <div className="absolute bottom-4 left-4">
              <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-black/60 text-white">
                {article.episode}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-foreground-700 leading-relaxed text-base">
              {article.fullDescription}
            </p>
          </div>

          {article.relatedShows && article.relatedShows.length > 0 && (
            <div className="border-t border-background-200 pt-10">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
                data-product-shop=""
              >
                {article.relatedShows.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-white rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer no-underline"
                  >
                    <div className="relative overflow-hidden aspect-[3/2]">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        title={`${rel.title} - 6789影视`}
                      />
                      <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px]">
                        <i className="ri-star-fill text-accent-500"></i>
                        {rel.rating}
                      </div>
                    </div>
                    <div className="p-3">
                      <span className="text-[11px] text-primary-500 font-medium">
                        {rel.category}
                      </span>
                      <h3 className="text-sm font-semibold text-foreground-950 mt-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}